'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Download, Copy, RefreshCw, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomTsvGeneratorContent } from '../../data/random-tsv-generator-content';
import Link from 'next/link';

export default function RandomTSVGenerator() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [rows, setRows] = useState(10);
    const [columns, setColumns] = useState(3);
    const [separator, setSeparator] = useState('\t');
    const [includeHeaders, setIncludeHeaders] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const { toast } = useToast();

    const generateRandomTSV = useCallback(() => {
        setIsGenerating(true);

        try {
            const headers = includeHeaders ? ['Column1', 'Column2', 'Column3'] : [];
            const data = [];

            for (let i = 0; i < rows; i++) {
                const row = [];
                for (let j = 0; j < columns; j++) {
                    const randomValue = Math.random().toString(36).substring(2, 15);
                    row.push(randomValue);
                }
                data.push(row);
            }

            let tsvContent = '';

            if (headers.length > 0) {
                tsvContent += headers.join(separator) + '\n';
            }

            data.forEach(row => {
                tsvContent += row.join(separator) + '\n';
            });

            setOutput(tsvContent);

            toast({
                title: 'TSV Generated!',
                description: `Generated ${rows} rows with ${columns} columns of random TSV data.`,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to generate TSV data.',
                variant: 'destructive',
            });
        } finally {
            setIsGenerating(false);
        }
    }, [rows, columns, separator, includeHeaders]);

    const downloadTSV = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/tab-separated-values' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'random-data.tsv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
            title: 'Download Started',
            description: 'TSV file is being downloaded.',
        });
    };

    const copyToClipboard = () => {
        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {
            toast({
                title: 'Copied!',
                description: 'TSV data has been copied to clipboard.',
            });
        });
    };

    const resetAll = () => {
        setInput('');
        setOutput('');
        setRows(10);
        setColumns(3);
        setSeparator('\t');
        setIncludeHeaders(true);
    };

    return (
        <>
            <Toaster />
            <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <ToolPageIntro title="Random TSV Generator">
                Generate random TSV data with our free online tool. Create test datasets, sample tab-separated files, and mock data for development and testing.
            </ToolPageIntro>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Input Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Input Options</CardTitle>
                                <CardDescription>
                                    Configure your TSV generation parameters
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Number of Rows
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="1000"
                                            value={rows}
                                            onChange={(e) => setRows(parseInt(e.target.value) || 1)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Number of Columns
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={columns}
                                            onChange={(e) => setColumns(parseInt(e.target.value) || 1)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Separator
                                        </label>
                                        <select
                                            value={separator}
                                            onChange={(e) => setSeparator(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="\t">Tab (\t)</option>
                                            <option value=",">Comma (,)</option>
                                            <option value=";">Semicolon (;)</option>
                                            <option value="|">Pipe (|)</option>
                                        </select>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="include-headers"
                                            checked={includeHeaders}
                                            onChange={(e) => setIncludeHeaders(e.target.checked)}
                                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="include-headers" className="text-sm text-gray-700">
                                            Include Headers
                                        </label>
                                    </div>

                                    <div className="space-x-2">
                                        <Button
                                            onClick={generateRandomTSV}
                                            disabled={isGenerating}
                                            className="flex-1"
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <FileText className="h-4 w-4 mr-2" />
                                                    Generate TSV
                                                </>
                                            )}
                                        </Button>

                                        <Button
                                            variant="outline"
                                            onClick={resetAll}
                                        >
                                            <RefreshCw className="h-4 w-4 mr-2" />
                                            Reset
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Output Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Generated TSV Data</CardTitle>
                                <CardDescription>
                                    Your randomly generated TSV data will appear here
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <pre className="text-sm text-gray-800 font-mono whitespace-pre-wrap break-all">
                                            {output || 'TSV data will appear here...'}
                                        </pre>
                                    </div>

                                    {output && (
                                        <div className="flex space-x-2">
                                            <Button
                                                onClick={copyToClipboard}
                                                variant="outline"
                                                size="sm"
                                            >
                                                <Copy className="h-4 w-4 mr-2" />
                                                Copy
                                            </Button>

                                            <Button
                                                onClick={downloadTSV}
                                                variant="outline"
                                                size="sm"
                                            >
                                                <Download className="h-4 w-4 mr-2" />
                                                Download
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomTsvGeneratorContent.title}
                description={randomTsvGeneratorContent.description}
                content={randomTsvGeneratorContent.content}
                faqs={randomTsvGeneratorContent.faqs}
                relatedTools={randomTsvGeneratorContent.relatedTools}
            />
        </>
    );
}
